/**
 * Exchange rate service for foreign currency → SEK conversion.
 *
 * Sources (per edge case 7.3 / Skatteverket):
 * 1. Riksbanken (primary, official Swedish rate)
 * 2. Frankfurter/ECB (fallback, free, no auth)
 *
 * All amounts returned as öre per unit of foreign currency.
 */

export interface ExchangeRate {
  readonly from: string;
  readonly to: string;
  readonly rate: number;
  readonly date: string;
  readonly source: 'riksbanken' | 'ecb';
}

// Riksbanken series codes for common currencies
const RIKSBANKEN_SERIES: Record<string, string> = {
  EUR: 'SEKEURPMI',
  USD: 'SEKUSDPMI',
  GBP: 'SEKGBPPMI',
  NOK: 'SEKNOKPMI',
  DKK: 'SEKDKKPMI',
  CHF: 'SEKCHFPMI',
  JPY: 'SEKJPYPMI',
  CAD: 'SEKCADPMI',
  AUD: 'SEKAUDPMI',
};

// Simple in-memory cache
const cache = new Map<string, { rate: ExchangeRate; fetchedAt: number }>();
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

/**
 * Get exchange rate for a currency pair on a specific date.
 * Returns how many SEK 1 unit of the foreign currency costs.
 *
 * Example: getExchangeRate('EUR', '2026-03-25') → { rate: 11.23, ... }
 * Meaning: 1 EUR = 11.23 SEK
 */
export async function getExchangeRate(currency: string, date: string): Promise<ExchangeRate> {
  const upper = currency.toUpperCase();
  if (upper === 'SEK') return { from: 'SEK', to: 'SEK', rate: 1, date, source: 'riksbanken' };

  const cacheKey = `${upper}_${date}`;
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
    return cached.rate;
  }

  // Browser kan inte anropa Riksbanken direkt pga CORS. Vi proxar via en
  // Supabase Edge Function (`fx-proxy`) som kör samma logik som Cloud Run-
  // pipelinen → identiska rates oavsett pipeline.
  const isBrowser = typeof window !== 'undefined';
  if (isBrowser) {
    try {
      const efUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fx-proxy?currency=${upper}&date=${date}`;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const r = await fetch(efUrl, {
        headers: anonKey ? { apikey: anonKey, Authorization: `Bearer ${anonKey}` } : {},
        signal: AbortSignal.timeout(5000),
      });
      if (r.ok) {
        const data = await r.json();
        if (typeof data?.rate === 'number' && data.rate > 0) {
          const rate: ExchangeRate = {
            from: upper,
            to: 'SEK',
            rate: data.rate,
            date,
            source: data.source === 'riksbanken' ? 'riksbanken' : 'ecb',
          };
          cache.set(cacheKey, { rate, fetchedAt: Date.now() });
          return rate;
        }
      }
    } catch {
      // fall through till lokal Frankfurter-fallback
    }
  } else {
    try {
      const rate = await fetchRiksbanken(upper, date);
      if (rate) {
        cache.set(cacheKey, { rate, fetchedAt: Date.now() });
        return rate;
      }
    } catch {
      // Fall through to Frankfurter
    }
  }

  // Sista fallback (browser om EF failer, server om Riksbanken failer)
  try {
    const rate = await fetchFrankfurter(upper, date);
    cache.set(cacheKey, { rate, fetchedAt: Date.now() });
    return rate;
  } catch (err) {
    throw new Error(
      `Kunde inte hämta växelkurs för ${upper}: ${err instanceof Error ? err.message : 'okänt fel'}`,
    );
  }
}

/**
 * Convert a foreign currency amount to SEK using the rate for a given date.
 * Returns amount in öre.
 */
export async function convertToSEK(
  amount: number,
  currency: string,
  date: string,
): Promise<{ amountSEK: number; rate: ExchangeRate }> {
  const rate = await getExchangeRate(currency, date);
  const amountSEK = Math.round(amount * rate.rate);
  return { amountSEK, rate };
}

/**
 * Get list of supported currencies.
 */
export function getSupportedCurrencies(): string[] {
  return ['EUR', 'USD', 'GBP', 'NOK', 'DKK', 'CHF', 'JPY', 'CAD', 'AUD'];
}

// ─── Riksbanken API ───

async function fetchRiksbanken(currency: string, date: string): Promise<ExchangeRate | null> {
  const series = RIKSBANKEN_SERIES[currency];
  if (!series) return null;

  // Riksbanken API returns the latest observation up to the given date
  const dateCompact = date.replace(/-/g, '');
  const url = `https://api.riksbank.se/swea/v1/Observations/${series}/${dateCompact}`;

  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(5000),
  });

  if (!response.ok) return null;

  const data = await response.json();

  // Riksbanken returns rate as "how many SEK for 1 unit" for most currencies
  // But for some (JPY) it's per 100 units
  if (!data || typeof data.value !== 'number') return null;

  let rate = data.value;

  // JPY is quoted per 100 units
  if (currency === 'JPY') rate = rate / 100;

  return {
    from: currency,
    to: 'SEK',
    rate,
    date: data.date ?? date,
    source: 'riksbanken',
  };
}

// ─── Frankfurter (ECB) API ───

async function fetchFrankfurter(currency: string, date: string): Promise<ExchangeRate> {
  // Frankfurter uses EUR as base. Special-case: when currency IS EUR we just need
  // EUR→SEK directly (Frankfurter doesn't return base→base).
  const symbols = currency === 'EUR' ? 'SEK' : `SEK,${currency}`;
  const url = `https://api.frankfurter.dev/v1/${date}?base=EUR&symbols=${symbols}`;

  const response = await fetch(url, {
    signal: AbortSignal.timeout(5000),
  });

  if (!response.ok) {
    throw new Error(`Frankfurter API svarade med ${response.status}`);
  }

  const data = await response.json();
  const rates = data.rates as Record<string, number>;

  const eurToSek = rates.SEK;
  if (!eurToSek) {
    throw new Error(`Frankfurter levererade ingen SEK-kurs för ${date}`);
  }

  let rate: number;
  if (currency === 'EUR') {
    rate = eurToSek; // 1 EUR = eurToSek SEK
  } else {
    const eurToCurrency = rates[currency];
    if (!eurToCurrency) {
      throw new Error(`Valutan ${currency} stöds inte av ECB`);
    }
    // 1 {currency} = (eurToSek / eurToCurrency) SEK
    rate = eurToSek / eurToCurrency;
  }

  return {
    from: currency,
    to: 'SEK',
    rate: Math.round(rate * 10000) / 10000, // 4 decimaler
    date: data.date ?? date,
    source: 'ecb',
  };
}
